import type { ChangeEvent, DOMAttributes, InputHTMLAttributes, ReactNode } from 'react';

import {
  controlClass,
  invalidControlClass,
  invalidTextareaClass,
  labelClass,
  textareaClass,
} from '@/shared/ui/formClasses';

// Gli attributi passano al controllo (input, textarea o select); gli eventi no, `onChange` a parte.
type FormFieldProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  keyof DOMAttributes<HTMLInputElement> | 'id' | 'value' | 'required' | 'className'
> & {
  id: string;
  label: ReactNode;
  value: string | number;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
  hint?: ReactNode;
  options?: { value: string; label: string; disabled?: boolean }[];
  rows?: number;
  /** Dentro l'input, a destra: un'icona, o un pulsante con `pointer-events-auto`. */
  trailing?: ReactNode;
  className?: string;
};

type FieldElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

/**
 * Un campo di modulo con etichetta, aiuto ed errore sotto. Con `options` è una
 * select, con `rows` una textarea, altrimenti un input; `className` si aggiunge
 * al controllo e il resto dei props va sul controllo.
 */
export default function FormField({
  id,
  label,
  value,
  onChange,
  required = false,
  error,
  hint,
  options,
  rows,
  trailing,
  className = '',
  'aria-describedby': extraDescribedBy,
  ...inputProps
}: FormFieldProps) {
  const describedBy = [extraDescribedBy, hint && `${id}-hint`, error && `${id}-error`]
    .filter(Boolean)
    .join(' ');
  const [base, invalid] = rows
    ? [textareaClass, invalidTextareaClass]
    : [controlClass, invalidControlClass];
  const controlProps = {
    id,
    value,
    onChange: (event: ChangeEvent<FieldElement>) => onChange(event.target.value),
    'aria-required': required || undefined,
    'aria-invalid': Boolean(error),
    'aria-describedby': describedBy || undefined,
    className: [error ? invalid : base, trailing && 'pr-10', className].filter(Boolean).join(' '),
  };
  return (
    <div>
      <label htmlFor={id} className={labelClass}>
        {label}
        {required && <span aria-hidden> *</span>}
      </label>
      {options ? (
        <select {...inputProps} {...controlProps}>
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
      ) : rows ? (
        <textarea rows={rows} {...inputProps} {...controlProps} />
      ) : trailing ? (
        <div className="relative">
          <input {...inputProps} {...controlProps} />
          <span className="pointer-events-none absolute inset-y-0 right-1 flex items-center">
            {trailing}
          </span>
        </div>
      ) : (
        <input {...inputProps} {...controlProps} />
      )}
      {hint && (
        <p id={`${id}-hint`} className="mt-1 text-caption text-subtle">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="mt-1 text-caption text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
